import { useState } from 'react'

import { Box, Button, SelectOption, Skeleton } from '@factorialco/deprecated-design-system'
import {
  GenerateVariableExportDocument,
  GenerateVariableExportMutation,
} from 'generated/resources/graphql'
import moment from 'moment'

import useLegalEntitiesOptions from 'modules/bookkeepers/hooks/useLegalEntitiesOptions'
import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

const ALL_PERIOD = 'all'
const CUSTOM_PERIOD = 'custom'
const DOCUMENT_FORMAT_XLSX = 'xlsx'
const DOCUMENT_FORMAT_CSV = 'csv'

const PayrollCycleExportModal = ({
  companyId,
  payrollRunId,
  countryCode,
  onClose,
}: {
  companyId: number | undefined
  payrollRunId: number
  countryCode: string
  onClose: () => void
}) => {
  const { access } = useCurrentSession()
  const [documentFormat, setDocumentFormat] = useState<string>(DOCUMENT_FORMAT_XLSX)
  const [variablesToInclude, setVariablesToInclude] = useState<string>(ALL_PERIOD)
  const [from, setFrom] = useState<moment.Moment>(moment().startOf('month'))
  const [to, setTo] = useState<moment.Moment>(moment().endOf('month'))
  const [legalEntityIds, setLegalEntityIds] = useState<string[]>([])

  const hasNoLegalEntitySelected = legalEntityIds.length < 1

  const [generateVariableExport, { loading: submitting }] = useMutation(
    GenerateVariableExportDocument,
    {
      extractorFn: (result: GenerateVariableExportMutation) =>
        result.bookkeepersManagementMutations
          .generateBookkeepersmanagementPayrollCycleVariableExport,
    }
  )

  const onSubmit = async () => {
    track(trackingEvents.bookkeeper.cycles.payrollVariablesExportedFromProfessionals, {
      company_id: companyId,
      document_format: documentFormat,
    })
    const response = await generateVariableExport({
      variables: {
        accessId: access?.id,
        companyId: companyId,
        format: documentFormat,
        fromDate: from.toISOString(),
        legalEntityIds: legalEntityIds.map((legalEntityId) => Number(legalEntityId)),
        payrollRunId: payrollRunId,
        toDate: to.toISOString(),
      },
    })

    const result = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('bookkeepers.clients.payroll_cycles.export_modal.result.error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('bookkeepers.clients.payroll_cycles.export_modal.result.success'),
      })
    }

    onClose()
  }

  const documentFormatOptions: SelectOption[] = [
    {
      label: i18n.t('bookkeepers.clients.payroll_cycles.export_modal.document_format.xlsx'),
      value: DOCUMENT_FORMAT_XLSX,
    },
    {
      label: i18n.t('bookkeepers.clients.payroll_cycles.export_modal.document_format.csv'),
      value: DOCUMENT_FORMAT_CSV,
    },
  ]

  const variablesToIncludeOptions: SelectOption[] = [
    {
      label: i18n.t(
        'bookkeepers.clients.payroll_cycles.export_modal.variables_included.options.all'
      ),
      value: ALL_PERIOD,
    },
    {
      label: i18n.t(
        'bookkeepers.clients.payroll_cycles.export_modal.variables_included.options.custom'
      ),
      value: CUSTOM_PERIOD,
    },
  ]

  const { loading, legalEntitiesOptions } = useLegalEntitiesOptions({ companyId, countryCode })

  return (
    <Modal
      illustration="document-edit"
      onClose={onClose}
      title={i18n.t('bookkeepers.clients.payroll_cycles.export_modal.title')}
      description={i18n.t('bookkeepers.clients.payroll_cycles.export_modal.description')}
      style={{ maxWidth: '360px' }}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={hasNoLegalEntitySelected || submitting}
          >
            {i18n.t('common.export')}
          </Button>
        </>
      }
    >
      {loading ? (
        <Box gap="s16">
          <Skeleton />
        </Box>
      ) : (
        <>
          <Box gap="s16">
            <SelectField
              key={documentFormat}
              label={i18n.t(
                'bookkeepers.clients.payroll_cycles.export_modal.document_format.label'
              )}
              value={documentFormat}
              options={documentFormatOptions}
              onChange={(value: string) => setDocumentFormat(value)}
            />

            <Selector.Popover
              mandatory
              isMultiSelect
              label={i18n.t('bookkeepers.clients.payroll_cycles.export_modal.legal_entities')}
              blankLabel=""
              searchPlaceholder=""
              placeholder={i18n.t('bookkeepers.clients.payroll_cycles.export_modal.placeholder')}
              value={legalEntityIds}
              onChange={(value: unknown) => {
                if (Array.isArray(value)) {
                  setLegalEntityIds(value)
                } else {
                  setLegalEntityIds([])
                }
              }}
            >
              {legalEntitiesOptions.map((option) => (
                <Selector.Option
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  searchValue={option.label}
                >
                  {option.label}
                </Selector.Option>
              ))}
            </Selector.Popover>

            <SelectField
              key={variablesToInclude}
              label={i18n.t(
                'bookkeepers.clients.payroll_cycles.export_modal.variables_included.label'
              )}
              value={variablesToInclude}
              options={variablesToIncludeOptions}
              onChange={(value: string) => setVariablesToInclude(value)}
            />

            {variablesToInclude === CUSTOM_PERIOD && (
              <Box gap="s16" flexDirection="row">
                <DayPicker
                  label={i18n.t(
                    'bookkeepers.clients.payroll_cycles.export_modal.variables_included.from'
                  )}
                  value={i18nDateToString(from, 'ui')}
                  onChange={(value: string) => setFrom(stringToDate(value, 'ui'))}
                />
                <DayPicker
                  label={i18n.t(
                    'bookkeepers.clients.payroll_cycles.export_modal.variables_included.to'
                  )}
                  value={i18nDateToString(to, 'ui')}
                  onChange={(value: string) => setTo(stringToDate(value, 'ui'))}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Modal>
  )
}

export default PayrollCycleExportModal
